import React, {useState} from 'react'
import classnames from "classnames";
import _ from 'lodash'

import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import Table from "@mui/material/Table";
import Box from "@mui/material/Box";
import {useDealContext} from "../../contexts/DealContext";
import {useMediaQuery} from "react-responsive";
import {mediaQueryForMobile} from "../HomePage/HomePage";
import TableHead from "@mui/material/TableHead";
import Collapse from "@mui/material/Collapse";
import {SectionChevron} from "./SectionChevron";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import {NumericFormat} from "react-number-format";
import {DealPageTaxItem} from "./DealPageTaxItem";
import Checkbox from "@mui/material/Checkbox";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import useDidMountEffect from "../../hooks/useDidMountEffect";


const UPFRONT_TAXES_STATES = ['NY', 'TX', 'GA', 'IL', 'MN', 'OH', 'AR', 'MD', 'OK', 'VA', 'NJ']
const SUPPLEMENTAL_TITLE_FEE_TAX_ID = 'supplemental_title_fee'

// Finance pays NJ's Supplemental Title Fee with the price, so its Cap can't change.
const isCapLocked = (tax, dealState) => dealState.is_purchase && tax.id === SUPPLEMENTAL_TITLE_FEE_TAX_ID

export const DealPageTaxes = () => {
    const { setDealStateWithUpdate, updateDealResponse, dealState, isHistorical } = useDealContext()
    const [open, setOpen] = useState(false);

    const isTabletOrMobile = useMediaQuery({ query: mediaQueryForMobile })

    // Master "cap all" toggle — mirrors the Fees section. Caps / uncaps every tax.
    const allTaxes = dealState?.formatted_quote?.taxes ?? []
    const [isAllCapped, setIsAllCapped] = useState(allTaxes.length > 0 && allTaxes.every(t => t.is_capped === true))
    useDidMountEffect(function setAllTaxesCapped(){
        let newDealState = structuredClone(dealState)
        newDealState.formatted_quote.taxes = newDealState.formatted_quote.taxes.map(t => ({
            ...structuredClone(t),
            is_capped: isCapLocked(t, dealState) ? t.is_capped : isAllCapped,
        }))
        setDealStateWithUpdate(newDealState, true)
    }, [isAllCapped])

    if (!(dealState && dealState?.formatted_quote)) return null;

    const isLoading = updateDealResponse?.isLoading
    // Cash deals are paid in full up front — no cap/upfront, so the Capped column
    // (and its master toggle) is dropped.
    const isCash = dealState.is_purchase && dealState.is_one_pay
    const isUpfrontTaxState = !dealState.is_purchase && UPFRONT_TAXES_STATES.includes(dealState.state)

    const tooltip = dealState.is_tax_exempt ? "Check to apply taxes" : "Uncheck for tax exempt"

    const taxes = dealState.formatted_quote.taxes.filter(t => t.amount !== '0')

    const salesTax = dealState.formatted_quote.taxes.find(t => t.name === 'Sales Tax')
    // Drives the interest/downpayment tax-cap scan properties. Now a TOP-LEVEL Deal
    // field (dealState.is_interest_tax_capped), mirroring is_tax_exempt — SEPARATE
    // from is_capped (the per-row Sales Tax switch), which stays on the tax object.
    const salesTaxInterestIsCapped = dealState.is_interest_tax_capped ?? false

    const totalTaxesAmount = taxes.reduce(function(accumulator, tax) {
        return accumulator + Number(tax.amount);
    }, 0)

    return (
        <Box className={classnames("dealPageTaxes", {
            mobile: isTabletOrMobile,
            open: open,
        })}>
            <Stack
                direction={'row'}
                justifyContent="space-between"
                alignItems="center"
                onClick={() => setOpen(!open)}
                style={{ cursor: 'pointer' }}
                className={'header header-row'}
            >
                <div
                    className={'header-name'}
                >
                        Taxes
                    <Tooltip title={tooltip} arrow placement={'top'}>
                        <Checkbox
                                className={classnames('payment-row_value taxes-checkbox', {
                                    isPurchase: dealState.is_purchase
                                })}
                                onChange={() => setDealStateWithUpdate({...dealState, is_tax_exempt: !dealState.is_tax_exempt}, true)}
                                checked={!dealState.is_tax_exempt}
                                disabled={isLoading || isHistorical}
                                icon={
                                    <svg className={'cb-unchecked'} width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <rect x="3" y="3" width="18" height="18" rx="3"/>
                                    </svg>
                                }
                                checkedIcon={
                                    // The rect/path are styled via the `.cb-checked`
                                    // class in SCSS rather than inline fill, so the
                                    // box color tracks var(--accent-blue) directly
                                    // without fighting MUI's own color cascade
                                    // through `currentColor`.
                                    <svg className={'cb-checked'} width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <rect x="3" y="3" width="18" height="18" rx="3"/>
                                        <path d="M8 12.5 l3 3 l5.5-6"/>
                                    </svg>
                                }
                            />
                    </Tooltip>
                </div>
                <Stack
                    direction={'row'}
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <div className={'header-amount'}>
                        <NumericFormat
                            value={_.round(totalTaxesAmount, 2)}
                            decimalScale={2}
                            displayType={'text'}
                            thousandSeparator={true}
                            prefix={'$'}
                        />
                    </div>
                    <div>
                        <IconButton
                            aria-label="expand row"
                            size="small"
                            onClick={() => setOpen(!open)}
                        >
                            <SectionChevron open={open}/>
                        </IconButton>
                    </div>
                </Stack>
            </Stack>
            <Collapse in={open} timeout="auto" unmountOnExit>
                {/*
                  Section-level "Tax Capped Taxes" toggle for is_interest_tax_capped
                  (drives TaxCappedInterestOnSalesTax + SurchargeOnCappedDownpaymentTax).
                  The original section toggle bound to is_capped was removed (Path 1) —
                  is_capped is now controlled solely by the per-row Sales Tax cap switch,
                  so nothing mirrors it here anymore.
                  PLACEMENT / LABEL ASSUMPTION (product owner to verify post-deploy):
                  this Stack alone controls position + label; the field and its scan
                  wiring are independent, so moving/renaming it is a self-contained edit.
                */}
                {false && isUpfrontTaxState && (
                    <Stack
                        direction={'row'}
                        alignItems="center"
                        style={{
                            height: '44px',
                            borderBottom: '1px solid var(--border)',
                        }}
                    >
                        <Box style={{ width: isTabletOrMobile ? '55%' : '70%', paddingLeft: '8px', fontSize: '13px', fontWeight: 500, color: 'var(--text-secondary)' }}>
                            Tax Capped Taxes
                        </Box>
                        <Box style={{ width: '15%', display: 'flex', justifyContent: 'center' }}>
                            {/* Tooltip is intentionally inverted: shows what clicking WILL do, not the current state */}
                            <Tooltip title={salesTaxInterestIsCapped ? "No" : "Yes"} arrow placement={'top'}>
                                <Switch
                                    checked={salesTaxInterestIsCapped}
                                    onChange={() => setDealStateWithUpdate({...dealState, is_interest_tax_capped: !salesTaxInterestIsCapped}, true)}
                                    disabled={isLoading || !salesTax || isHistorical}
                                />
                            </Tooltip>
                        </Box>
                    </Stack>
                )}
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell
                                className={'header-cell'}
                                align="left"
                                width={isCash ? '70%' : '55%'}
                            >
                                Name
                            </TableCell>
                            {
                                !isTabletOrMobile &&
                                <TableCell
                                    className={'header-cell'}
                                    align="center"
                                    width={'15%'}
                                >
                                    Taxable Amt
                                </TableCell>
                            }
                            {
                                !isCash &&
                                <TableCell
                                    className={'header-cell'}
                                    align="center"
                                    width={'15%'}
                                >
                                    <div className={'capped-header'}>
                                        <Tooltip title={isAllCapped ? "Uncap All" : "Cap All"} arrow placement={'top'}>
                                            <Switch
                                                disabled={isLoading || isHistorical}
                                                checked={isAllCapped}
                                                onChange={() => setIsAllCapped(!isAllCapped)}
                                            />
                                        </Tooltip>
                                    </div>
                                </TableCell>
                            }
                            <TableCell
                                className={'header-cell'}
                                align="center"
                                width={isTabletOrMobile ? '30%' : '15%'}
                            >
                                Tax
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {/*<DealPageMonthlyTaxItem/>*/}
                        {
                            taxes.map((tax, idx) => {
                                return (
                                    <DealPageTaxItem
                                        key={tax.name}
                                        taxItem={tax}
                                        isCappingDisabled={isCash}
                                        isCapLocked={isCapLocked(tax, dealState)}
                                    />
                                )
                            })
                        }
                    </TableBody>
                </Table>
            </Collapse>
        </Box>
    )
}